import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Wallet,
  Building2,
  CreditCard,
  Plus,
  Check,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowDownToLine,
  History,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countries = [
  "France", "Germany", "Spain", "Italy", "Netherlands", "Belgium", 
  "Portugal", "Austria", "Switzerland", "United Kingdom", "United States",
  "Canada", "Australia", "Japan", "Other"
];

export default function WithdrawPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState({ available: 0, pending: 0, total_withdrawn: 0 });
  const [payoutMethods, setPayoutMethods] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [methodType, setMethodType] = useState("bank");
  const [editingMethod, setEditingMethod] = useState(null);
  const [nextDate, setNextDate] = useState({ is_tuesday: false, next_date: "" });

  const [bankForm, setBankForm] = useState({
    full_name: "",
    iban: "",
    bic: "",
    bank_name: "",
    country: "France",
  });

  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    method_id: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [balanceRes, methodsRes, withdrawalsRes, dateRes] = await Promise.all([
        axios.get(`${API}/withdraw/balance`, { withCredentials: true }),
        axios.get(`${API}/withdraw/methods`, { withCredentials: true }),
        axios.get(`${API}/withdraw/history`, { withCredentials: true }),
        axios.get(`${API}/withdraw/next-date`, { withCredentials: true }),
      ]);
      setBalance(balanceRes.data);
      setPayoutMethods(methodsRes.data);
      setWithdrawals(withdrawalsRes.data);
      setNextDate(dateRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount || 0);
  };

  const resetForms = () => {
    setBankForm({ full_name: "", iban: "", bic: "", bank_name: "", country: "France" });
    setEditingMethod(null);
  };

  const handleSaveMethod = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = { type: "bank", ...bankForm };

      if (editingMethod) {
        await axios.put(`${API}/withdraw/methods/${editingMethod.id}`, payload, { withCredentials: true });
        toast.success(t("withdraw.methodUpdated"));
      } else {
        await axios.post(`${API}/withdraw/methods`, payload, { withCredentials: true });
        toast.success(t("withdraw.methodAdded"));
      }

      fetchData();
      setDialogOpen(false);
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("withdraw.failedToSave"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMethod = async (methodId) => {
    if (!window.confirm(t("withdraw.confirmDeleteMethod"))) return;
    
    try {
      await axios.delete(`${API}/withdraw/methods/${methodId}`, { withCredentials: true });
      toast.success(t("withdraw.methodDeleted"));
      fetchData();
    } catch (error) {
      toast.error(t("withdraw.failedToDelete"));
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/withdraw/request`, {
        amount: parseFloat(withdrawForm.amount),
        method_id: withdrawForm.method_id,
      }, { withCredentials: true });
      
      toast.success(t("withdraw.withdrawalSubmitted"));
      setWithdrawDialogOpen(false);
      setWithdrawForm({ amount: "", method_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("withdraw.failedToRequest"));
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (method) => {
    setEditingMethod(method);
    setMethodType("bank");
    setBankForm({
      full_name: method.full_name,
      iban: method.iban,
      bic: method.bic,
      bank_name: method.bank_name,
      country: method.country,
    });
    setDialogOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "pending": return <Clock className="h-4 w-4 text-amber-500" />;
      case "failed": return <XCircle className="h-4 w-4 text-destructive" />;
      default: return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed": return t("withdraw.completed");
      case "pending": return t("withdraw.pending");
      case "failed": return t("withdraw.failed");
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-in fade-in duration-500">
        <div className="space-y-2">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="withdraw-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("withdraw.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("withdraw.subtitle")}</p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-border bg-gradient-to-br from-emerald-500/10 to-emerald-600/5">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{t("withdraw.availableBalance")}</p>
                <p className="text-2xl sm:text-3xl font-bold mt-2 text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(balance.available)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/20">
                <Wallet className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{t("withdraw.pendingBalance")}</p>
                <p className="text-2xl sm:text-3xl font-bold mt-2">{formatCurrency(balance.pending)}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10">
                <Clock className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">{t("withdraw.totalWithdrawn")}</p>
                <p className="text-2xl sm:text-3xl font-bold mt-2">{formatCurrency(balance.total_withdrawn)}</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <ArrowDownToLine className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Withdraw Button + Tuesday Info */}
      <div className="space-y-3">
        {!nextDate.is_tuesday && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20" data-testid="tuesday-notice">
            <Clock className="h-5 w-5 text-amber-500 shrink-0" />
            <div>
              <p className="text-sm font-medium">{t("adminWithdraw.withdrawalDay")}</p>
              <p className="text-xs text-muted-foreground">{t("adminWithdraw.nextWithdrawal")}: <span className="font-semibold">{nextDate.next_date}</span></p>
            </div>
          </div>
        )}
        {nextDate.is_tuesday && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20" data-testid="tuesday-open-notice">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
            <p className="text-sm font-medium">{t("withdraw.withdrawalsOpen")}</p>
          </div>
        )}
        <div className="flex gap-4">
        <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="h-12 px-8 rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
              disabled={!nextDate.is_tuesday || balance.available < 1 || payoutMethods.length === 0}
              data-testid="withdraw-btn"
            >
              <ArrowDownToLine className="h-5 w-5 mr-2" />
              {t("withdraw.withdrawFunds")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("withdraw.withdrawFunds")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleWithdraw} className="space-y-5 pt-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t("withdraw.payoutMethod")}</Label>
                <Select
                  value={withdrawForm.method_id}
                  onValueChange={(value) => setWithdrawForm({ ...withdrawForm, method_id: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl">
                    <SelectValue placeholder={t("withdraw.payoutMethod")} />
                  </SelectTrigger>
                  <SelectContent>
                    {payoutMethods.map((method) => (
                      <SelectItem key={method.id} value={method.id}>
                        {method.bank_name} (****{method.iban?.slice(-4)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">{t("withdraw.withdrawAmount")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  max={balance.available}
                  value={withdrawForm.amount}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="h-12 rounded-xl text-lg"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {t("withdraw.minWithdraw")}: €1.00 • {t("withdraw.available")}: {formatCurrency(balance.available)}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-11 rounded-xl"
                  onClick={() => setWithdrawDialogOpen(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 rounded-xl"
                  disabled={saving || !withdrawForm.method_id || !withdrawForm.amount}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("withdraw.requestWithdraw")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Payout Methods & History */}
      <Tabs defaultValue="methods" className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px] h-12 rounded-xl p-1">
          <TabsTrigger value="methods" className="rounded-lg">
            <CreditCard className="h-4 w-4 mr-2" />
            {t("withdraw.payoutMethod")}
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg">
            <History className="h-4 w-4 mr-2" />
            {t("withdraw.history")}
          </TabsTrigger>
        </TabsList>

        {/* Payout Methods Tab */}
        <TabsContent value="methods" className="mt-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">{t("withdraw.payoutMethod")}</CardTitle>
                <CardDescription>{t("withdraw.managePayoutMethods")}</CardDescription>
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForms(); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-xl" onClick={resetForms}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("withdraw.addMethod")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>
                      {editingMethod ? t("withdraw.editAccount") : t("withdraw.addMethod")}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveMethod} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>{t("withdraw.fullName")}</Label>
                      <Input
                        value={bankForm.full_name}
                        onChange={(e) => setBankForm({ ...bankForm, full_name: e.target.value })}
                        placeholder={t("withdraw.fullNamePlaceholder")}
                        className="h-11 rounded-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("withdraw.iban")}</Label>
                      <Input
                        value={bankForm.iban}
                        onChange={(e) => setBankForm({ ...bankForm, iban: e.target.value.toUpperCase() })}
                        placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                        className="h-11 rounded-xl font-mono"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("withdraw.bic")}</Label>
                      <Input
                        value={bankForm.bic}
                        onChange={(e) => setBankForm({ ...bankForm, bic: e.target.value.toUpperCase() })}
                        placeholder="XXXXXXXX"
                        className="h-11 rounded-xl font-mono"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("withdraw.bankName")}</Label>
                      <Input
                        value={bankForm.bank_name}
                        onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                        placeholder={t("withdraw.bankNamePlaceholder")}
                        className="h-11 rounded-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("withdraw.country")}</Label>
                      <Select
                        value={bankForm.country}
                        onValueChange={(value) => setBankForm({ ...bankForm, country: value })}
                      >
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" className="flex-1 h-11 rounded-xl" onClick={() => setDialogOpen(false)}>
                        {t("common.cancel")}
                      </Button>
                      <Button type="submit" className="flex-1 h-11 rounded-xl" disabled={saving}>
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-2" />{t("withdraw.saveAccount")}</>}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {payoutMethods.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="font-medium text-muted-foreground">{t("withdraw.noMethodsAdded")}</p>
                  <p className="text-sm text-muted-foreground mt-1">{t("withdraw.addMethodDesc")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {payoutMethods.map((method) => (
                    <div
                      key={method.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-blue-500/10">
                          <Building2 className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {method.bank_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {method.full_name} &bull; ****{method.iban?.slice(-4)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" className="rounded-lg" onClick={() => openEditDialog(method)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="rounded-lg text-destructive hover:text-destructive" onClick={() => handleDeleteMethod(method.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-lg">{t("withdraw.history")}</CardTitle>
              <CardDescription>{t("withdraw.historyDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              {withdrawals.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                    <History className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="font-medium text-muted-foreground">{t("withdraw.noHistory")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {withdrawals.map((withdrawal) => (
                    <div
                      key={withdrawal.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                    >
                      <div className="flex items-center gap-4">
                        {getStatusIcon(withdrawal.status)}
                        <div>
                          <p className="font-medium">{formatCurrency(withdrawal.amount)}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(withdrawal.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                          withdrawal.status === "completed" ? "bg-emerald-500/10 text-emerald-600" :
                          withdrawal.status === "pending" ? "bg-amber-500/10 text-amber-600" :
                          "bg-destructive/10 text-destructive"
                        }`}>
                          {getStatusText(withdrawal.status)}
                        </span>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("withdraw.bankTransfer")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
